import React, { useCallback, useEffect, useRef, useState } from 'react'
import MapGL, { Layer, Marker, Source } from 'react-map-gl'
import 'mapbox-gl/dist/mapbox-gl.css'
import { gql, useSubscription } from '@apollo/client'
import { Box, Button, CircularProgress, Drawer, IconButton, Link, Tooltip } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined'
import SmsOutlinedIcon from '@mui/icons-material/SmsOutlined'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded'
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined'
import PersonSearchOutlinedIcon from '@mui/icons-material/PersonSearchOutlined'
import ShoppingBagOutlinedIcon from '@mui/icons-material/ShoppingBagOutlined'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import { subscriptionRiderLocation } from '../../apollo/subscriptions'
import { getDirections, searchPlaces } from '../../utils/mapbox'
import paymentLabel from '../../utils/paymentLabel'
import { AGE, C, MONO, TONE, primaryButtonSx, tnum } from './tokens'
import {
  NEXT_STEP, ageLevel, canAssign, canSendToFirstDelivery, canSendToRushmyfood, dueAt, findingRushmyfoodDriver, fullStamp, haversineMiles, isActive, isInTransit, isLate,
  isStale, money, relative, smartTime, span, statusMeta, timeShort,
} from './model'
import { AgeBadge, ChannelBadge, PersonAvatar, RestaurantLogo, StatusBadge } from './Badges'
import { AddressActions, AssignButton } from './cells'
import { useDispatchUi } from './context'

const SUB_RIDER_LOCATION = gql`${subscriptionRiderLocation}`

const ROUTE_LAYER_STYLE = {
  id: 'dispatch-route-line',
  type: 'line',
  layout: { 'line-join': 'round', 'line-cap': 'round' },
  paint: { 'line-color': TONE.transit.dot, 'line-width': 4, 'line-opacity': 0.9 },
}

const pin = (bg, size = 24, square) => ({
  width: size, height: size, borderRadius: square ? 6 : '50%', background: bg, color: '#fff', border: '2px solid #fff',
  boxShadow: '0 1px 4px rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700,
})

// Addresses already looked up, so walking back over an order doesn't ask Mapbox again.
const geocoded = new Map()

/**
 * Where the dropoff is. Our orders carry coordinates; an old-app order has only
 * the address, so it's looked up the same way the admin's address search does,
 * biased towards the restaurant.
 */
function useDropoffLocation(view, token) {
  const full = view.address?.full || null
  const known = view.address?.location || null
  const near = view.restaurant.location
  const [found, setFound] = useState(() => (full ? geocoded.get(full) : null) || null)

  useEffect(() => {
    if (known || !full || !token) return undefined
    if (geocoded.has(full)) {
      setFound(geocoded.get(full))
      return undefined
    }
    let cancelled = false
    setFound(null)
    searchPlaces(full, token, { limit: 1, ...(near ? { proximity: `${near.lng},${near.lat}` } : {}) })
      .then(hits => {
        const hit = hits[0] ? { lat: hits[0].lat, lng: hits[0].lng } : null
        geocoded.set(full, hit)
        if (!cancelled) setFound(hit)
      })
      .catch(() => {})
    return () => { cancelled = true }
  }, [full, known, token, near?.lat, near?.lng])

  return known || found
}

/** Restaurant → customer on Mapbox, with the driver's live position when there is one. */
function RouteMap({ view, mapboxToken, liveDriver, onRoute }) {
  const rest = view.restaurant.location
  const cust = view.address?.location
  const driver = liveDriver || view.driver?.location || null
  const mapRef = useRef(null)
  const [route, setRoute] = useState(null)

  useEffect(() => {
    if (!mapboxToken || !rest || !cust) {
      setRoute(null)
      return undefined
    }
    let cancelled = false
    getDirections(rest, cust, mapboxToken)
      .then(res => {
        if (!cancelled) {
          setRoute(res)
          onRoute(res)
        }
      })
      .catch(() => {})
    return () => { cancelled = true }
  }, [mapboxToken, rest?.lat, rest?.lng, cust?.lat, cust?.lng])

  const fit = useCallback(() => {
    const map = mapRef.current
    if (!map) return
    const pts = [rest, cust, driver].filter(Boolean)
    if (pts.length === 0) return
    if (pts.length === 1) {
      map.easeTo({ center: [pts[0].lng, pts[0].lat], zoom: 13 })
      return
    }
    const lngs = pts.map(p => p.lng)
    const lats = pts.map(p => p.lat)
    map.fitBounds([[Math.min(...lngs), Math.min(...lats)], [Math.max(...lngs), Math.max(...lats)]], { padding: 36, duration: 400 })
  }, [rest?.lat, rest?.lng, cust?.lat, cust?.lng, driver?.lat, driver?.lng])

  useEffect(() => { fit() }, [fit])

  if (!mapboxToken || (!rest && !cust)) {
    return (
      <Box sx={{ height: 176, borderRadius: '10px', border: `1px solid ${C.border}`, bgcolor: C.mutedBg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, color: C.muted }}>
        Map unavailable
      </Box>
    )
  }
  const center = driver || rest || cust
  return (
    <Box sx={{ height: 176, borderRadius: '10px', overflow: 'hidden', border: `1px solid ${C.border}` }}>
      <MapGL
        ref={mapRef}
        mapboxAccessToken={mapboxToken}
        initialViewState={{ longitude: center.lng, latitude: center.lat, zoom: 13 }}
        style={{ width: '100%', height: '100%' }}
        mapStyle="mapbox://styles/mapbox/light-v11"
        onLoad={fit}
      >
        {route?.geojson && (
          <Source id="dispatch-route" type="geojson" data={route.geojson}>
            <Layer {...ROUTE_LAYER_STYLE} />
          </Source>
        )}
        {rest && <Marker longitude={rest.lng} latitude={rest.lat} anchor="center"><div style={pin(C.brandRed, 24, true)}>R</div></Marker>}
        {cust && <Marker longitude={cust.lng} latitude={cust.lat} anchor="center"><div style={pin('#1d4ed8')}>C</div></Marker>}
        {driver && <Marker longitude={driver.lng} latitude={driver.lat} anchor="center"><div style={pin(C.brandGreen, 18)} /></Marker>}
      </MapGL>
    </Box>
  )
}

function Section({ title, children, aside }) {
  return (
    <Box component="section" sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box component="h3" sx={{ m: 0, fontSize: 11, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: C.muted }}>{title}</Box>
        {aside}
      </Box>
      {children}
    </Box>
  )
}

function Callout({ tone, icon, children }) {
  const colors = tone === 'late' ? { bg: AGE.late.bg, fg: AGE.late.ink } : tone === 'warn' ? { bg: AGE.warn.bg, fg: AGE.warn.ink } : { bg: C.mutedBg, fg: C.muted }
  return (
    <Box sx={{ display: 'flex', gap: 1, px: 1.25, py: 1, borderRadius: '8px', fontSize: 12, lineHeight: 1.5, bgcolor: colors.bg, color: colors.fg, '& svg': { fontSize: 15, mt: '1px', flexShrink: 0 } }}>
      {icon}
      <span>{children}</span>
    </Box>
  )
}

function Line({ label, value, strong, sub, color }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: strong ? 14 : 13, fontWeight: strong ? 600 : 400, color: color || (strong ? C.text : C.muted), pl: sub ? 2 : 0, ...tnum }}>
      <span>{label}</span>
      <Box component="span" sx={{ color: color || C.text }}>{value}</Box>
    </Box>
  )
}

function ContactRow({ avatar, title, lines, phone, sms, extra }) {
  const digits = phone ? String(phone).replace(/[^\d+]/g, '') : null
  const btn = { height: 30, minWidth: 0, px: 1, borderRadius: '8px', textTransform: 'none', fontSize: 12, fontWeight: 500, color: C.text, borderColor: C.border, '&:hover': { bgcolor: C.mutedBg, borderColor: C.input } }
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, px: 1.5, py: 1.25 }}>
      {avatar}
      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
        <Box sx={{ fontSize: 13, fontWeight: 500, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{title}</Box>
        {lines.filter(Boolean).map(l => <Box key={l} sx={{ fontSize: 12, color: C.muted }}>{l}</Box>)}
        {phone && <Box sx={{ fontSize: 12, color: C.muted, ...tnum }}>{phone}</Box>}
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
        {extra}
        {digits && sms && (
          <Tooltip title={`Text ${title}`} arrow>
            <Button variant="outlined" component="a" href={`sms:${digits}`} sx={{ ...btn, px: 0.75 }} aria-label={`Text ${title}`}>
              <SmsOutlinedIcon sx={{ fontSize: 16 }} />
            </Button>
          </Tooltip>
        )}
        {digits && (
          <Button variant="outlined" component="a" href={`tel:${digits}`} startIcon={<PhoneOutlinedIcon sx={{ fontSize: '15px !important' }} />} sx={{ ...btn, '& .MuiButton-startIcon': { mr: 0.5 } }}>
            Call
          </Button>
        )}
      </Box>
    </Box>
  )
}

function DeliveryBlock({ view }) {
  const ui = useDispatchUi()
  const o = view.raw
  const box = { display: 'flex', alignItems: 'center', gap: 1.5, px: 1.5, py: 1.25, borderRadius: '10px', border: `1px solid ${C.border}` }

  if (view.type === 'PICKUP') {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: 13, color: C.muted }}>
        <ShoppingBagOutlinedIcon sx={{ fontSize: 17 }} />
        {view.status === 'COLLECTED' ? 'The customer collected this' : view.typeLabel === 'Pickup' ? 'Customer picks this up' : `${view.typeLabel} order`} — no driver needed.
      </Box>
    )
  }
  if (view.driver) {
    return (
      <Box sx={box}>
        <PersonAvatar name={view.driver.name} photo={view.driver.photo} size={32} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ fontSize: 13, fontWeight: 500, color: C.text }}>{view.driver.name}</Box>
          <Box sx={{ fontSize: 12, color: C.muted }}>
            {[view.driver.transport || o.rider?.driverType, isInTransit(view) ? statusMeta(view.status).label.toLowerCase() : null].filter(Boolean).join(' · ')}
          </Box>
        </Box>
        <ChannelBadge channel={view.channel} />
      </Box>
    )
  }
  if (view.awaitingCourier && isActive(view)) {
    return (
      <Box sx={{ ...box, borderStyle: 'dashed' }}>
        <Box sx={{ width: 32, height: 32, borderRadius: '50%', bgcolor: C.mutedBg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <LocalShippingOutlinedIcon sx={{ fontSize: 17, color: C.muted }} />
        </Box>
        {/* A restaurant's board never names First Delivery: to them it's RushMyFood. */}
        <Box sx={{ flex: 1 }}>
          <Box sx={{ fontSize: 13, fontWeight: 500, color: C.text }}>{ui.firstDelivery ? 'Waiting on First Delivery' : 'Finding a RushMyFood driver'}</Box>
          <Box sx={{ fontSize: 12, color: C.muted }}>{ui.firstDelivery ? 'Their courier shows here once they name one.' : 'The driver shows here once one is on the way.'}</Box>
        </Box>
        <ChannelBadge channel={view.external ? view.channel : ui.firstDelivery ? 'FIRST_DELIVERY' : 'RUSHMYFOOD'} />
      </Box>
    )
  }
  if (view.external) {
    // An old-app or email delivery RushMyFood carried without naming the driver to us.
    return (
      <Box sx={box}>
        <Box sx={{ width: 32, height: 32, borderRadius: '50%', bgcolor: C.mutedBg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <LocalShippingOutlinedIcon sx={{ fontSize: 17, color: C.muted }} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Box sx={{ fontSize: 13, fontWeight: 500, color: C.text }}>{view.sourceLabel} delivery</Box>
          <Box sx={{ fontSize: 12, color: C.muted }}>No driver details on record.</Box>
        </Box>
        <ChannelBadge channel={view.channel} />
      </Box>
    )
  }
  if (canAssign(view)) {
    return (
      <Box sx={{ ...box, borderStyle: 'dashed', flexWrap: 'wrap' }}>
        <Box sx={{ width: 32, height: 32, borderRadius: '50%', bgcolor: AGE.warn.bg, color: AGE.warn.ink, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <PersonSearchOutlinedIcon sx={{ fontSize: 17 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 140 }}>
          <Box sx={{ fontSize: 13, fontWeight: 500, color: C.text }}>No driver yet</Box>
          <Box sx={{ fontSize: 12, color: C.muted }}>
            {ui.rushmyfood && findingRushmyfoodDriver(view)
              ? 'RushMyFood is finding a driver.'
              : view.channel === 'STAFF'
                ? 'Set for restaurant staff delivery.'
                : ui.firstDelivery ? 'Assign one of ours, or hand it to First Delivery.' : 'Assign one of your drivers.'}
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 0.75 }}>
          {ui.rushmyfood && canSendToRushmyfood(view) && (
            <Button
              size="small"
              variant="outlined"
              onClick={() => ui.requestRushmyfood([view])}
              sx={{ height: 28, textTransform: 'none', fontSize: 12, fontWeight: 500, borderRadius: '8px', color: C.text, borderColor: C.border }}
            >
              Send to RushMyFood Delivery
            </Button>
          )}
          {ui.firstDelivery && canSendToFirstDelivery(view) && (
            <Button
              size="small"
              variant="outlined"
              onClick={() => ui.requestFirstDelivery([view])}
              sx={{ height: 28, textTransform: 'none', fontSize: 12, fontWeight: 500, borderRadius: '8px', color: C.text, borderColor: C.border }}
            >
              First Delivery
            </Button>
          )}
          <AssignButton view={view} prominent />
        </Box>
      </Box>
    )
  }
  return <Box sx={{ fontSize: 13, color: C.muted }}>No driver was assigned.</Box>
}

const UPCOMING = {
  DELIVERY: ['ACCEPTED', 'PREPARED', 'ASSIGNED', 'PICKED', 'DELIVERED'],
  PICKUP: ['ACCEPTED', 'PREPARED', 'PICKED'],
}

/** What a step is called on this order: for a pickup, PICKED is the customer collecting it. */
const stepLabel = (view, status) => {
  if (status === 'PENDING') return 'Placed'
  if (status === 'PICKED' && view.type === 'PICKUP') return 'Collected by customer'
  return statusMeta(status).label
}
const stepTone = (view, status) =>
  TONE[statusMeta(status === 'PICKED' && view.type === 'PICKUP' ? 'COLLECTED' : status).tone] || TONE.muted

function Timeline({ view, now }) {
  const seen = new Set(view.history.map(h => h.status))
  const reached = statusMeta(view.status).step
  const upcoming = isActive(view) && view.status !== 'SCHEDULED'
    ? UPCOMING[view.type].filter(s => !seen.has(s) && statusMeta(s).step > reached)
    : []
  const est = {
    PICKED: view.type === 'DELIVERY' && view.pickup && !view.pickup.done ? view.pickup.at : null,
    PREPARED: view.type === 'PICKUP' && view.pickup && !view.pickup.done ? view.pickup.at : null,
    DELIVERED: view.dropoff && !view.dropoff.done ? view.dropoff.at : null,
  }
  const stale = isStale(view, now)
  const tz = view.restaurant?.timezone
  const stamp = at => (smartTime(at, now, tz) === timeShort(at, tz) ? timeShort(at, tz) : `${smartTime(at, now, tz)}, ${timeShort(at, tz)}`)

  return (
    <Box component="ol" sx={{ m: 0, p: 0, listStyle: 'none', display: 'flex', flexDirection: 'column' }}>
      {view.history.map((h, i) => {
        const t = stepTone(view, h.status)
        const prev = view.history[i - 1]
        const lastOne = i === view.history.length - 1 && upcoming.length === 0
        return (
          <Box component="li" key={`${h.status}-${h.at}`} sx={{ position: 'relative', display: 'flex', gap: 1.5, pb: lastOne ? 0 : 1.75 }}>
            {!lastOne && <Box aria-hidden sx={{ position: 'absolute', left: '5px', top: 14, bottom: 0, width: '1px', bgcolor: C.border }} />}
            <Box aria-hidden sx={{ position: 'relative', mt: '4px', width: 11, height: 11, borderRadius: '50%', bgcolor: h.status === 'PENDING' ? C.text : t.dot, boxShadow: `0 0 0 3px ${C.card}`, flexShrink: 0 }} />
            <Box sx={{ flex: 1, display: 'flex', justifyContent: 'space-between', gap: 1.5, fontSize: 13 }}>
              <Box component="span" sx={{ fontWeight: 500, color: C.text }}>{stepLabel(view, h.status)}</Box>
              <Box sx={{ textAlign: 'right', fontSize: 12, ...tnum }}>
                <Box sx={{ color: C.text }}>{stamp(h.at)}</Box>
                {prev && <Box sx={{ color: C.muted }}>+{span(h.at - prev.at)}</Box>}
              </Box>
            </Box>
          </Box>
        )
      })}
      {upcoming.map((s, i) => (
        <Box component="li" key={s} sx={{ position: 'relative', display: 'flex', gap: 1.5, pb: i === upcoming.length - 1 ? 0 : 1.75 }}>
          {i < upcoming.length - 1 && <Box aria-hidden sx={{ position: 'absolute', left: '5px', top: 14, bottom: 0, borderLeft: `1px dashed ${C.input}` }} />}
          <Box aria-hidden sx={{ position: 'relative', mt: '4px', width: 11, height: 11, borderRadius: '50%', bgcolor: C.card, boxShadow: `inset 0 0 0 1.5px ${C.input}`, flexShrink: 0 }} />
          <Box sx={{ flex: 1, display: 'flex', justifyContent: 'space-between', gap: 1.5, fontSize: 13, color: C.muted }}>
            <span>{stepLabel(view, s)}</span>
            {est[s] && (
              <Box component="span" sx={{ fontSize: 12, ...tnum, ...(now > est[s] && !stale ? { color: AGE.late.ink, fontWeight: 600 } : null) }}>
                est. {timeShort(est[s], tz)}
              </Box>
            )}
          </Box>
        </Box>
      ))}
    </Box>
  )
}

function ExternalPayment({ view }) {
  const { lines, caption } = view.payment
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
      {lines.map(([label, value, tone]) => <Line key={label} label={label} value={value} color={tone === 'credit' ? TONE.delivered.fg : undefined} />)}
      <Box sx={{ my: 0.5, borderTop: `1px solid ${C.border}` }} />
      <Line strong label="Total" value={view.total === 0 ? <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color: AGE.warn.ink }}><WarningAmberRoundedIcon sx={{ fontSize: 16 }} />{money(0)}</Box> : money(view.total)} />
      {caption && <Box sx={{ mt: 0.5, fontSize: 12, color: C.muted, whiteSpace: 'pre-line' }}>{caption}</Box>}
    </Box>
  )
}

/** The old Payment tab, line for line. */
function Payment({ view, items }) {
  if (view.payment) return <ExternalPayment view={view} />
  const o = view.raw
  const itemsSum = (items || []).reduce((sum, it) => {
    const base = parseFloat(it.variation?.price || 0)
    const addonsSum = (it.addons || []).reduce((a, ad) => a + (ad.options || []).reduce((b, op) => b + parseFloat(op.price || 0), 0), 0)
    return sum + (base + addonsSum) * (parseInt(it.quantity, 10) || 1)
  }, 0)
  const subtotal = itemsSum || parseFloat(o.orderAmount || 0)
  const total = parseFloat(o.paidAmount || 0)
  // A manual delivery: the customer paid the restaurant food, tax and tip. Its
  // delivery fee is what RushMyFood charges the restaurant, not the customer.
  const manual = o.orderSource === 'MANUAL'
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
      <Line label="Subtotal" value={money(subtotal)} />
      {!manual && <Line label="Delivery" value={money(o.deliveryCharges)} />}
      <Line label="Tax" value={money(o.taxationAmount)} />
      <Line label="Tip" value={money(o.tipping)} />
      {parseFloat(o.driverTip || 0) > 0 && <Line sub label="Driver tip" value={money(o.driverTip)} />}
      {parseFloat(o.serverTip || 0) > 0 && <Line sub label="Server tip" value={money(o.serverTip)} />}
      {o.promotion && <Line label={`Promo (${o.promotion.code})`} value={`-${money(o.promotion.discountAmount)}`} color={TONE.delivered.fg} />}
      {o.refundedAmount > 0 && <Line label="Refunded" value={`-${money(o.refundedAmount)}`} color={AGE.late.ink} />}
      <Box sx={{ my: 0.5, borderTop: `1px solid ${C.border}` }} />
      <Line strong label="Total" value={total === 0 ? <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color: AGE.warn.ink }}><WarningAmberRoundedIcon sx={{ fontSize: 16 }} />{money(0)}</Box> : money(total)} />
      <Box sx={{ mt: 0.5, fontSize: 12, color: C.muted }}>{[paymentLabel(o), o.paymentStatus].filter(Boolean).join(' · ')}</Box>
      {manual && o.manualDeliveryFee != null && (
        <Box sx={{ mt: 0.25, fontSize: 12, color: C.muted }}>
          If RushMyFood delivers it, the restaurant is charged {money(o.manualDeliveryFee)} + the tip.
        </Box>
      )}
    </Box>
  )
}

function Items({ items, loading }) {
  if (loading && !items.length) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={18} /></Box>
  if (!items.length) return <Box sx={{ fontSize: 13, color: C.muted }}>No items on this order.</Box>
  return (
    <Box sx={{ border: `1px solid ${C.border}`, borderRadius: '10px' }}>
      {items.map((it, i) => (
        <Box key={it._id || i} sx={{ display: 'flex', gap: 1.5, px: 1.5, py: 1.25, borderTop: i ? `1px solid ${C.borderSoft}` : 0 }}>
          <Box sx={{ width: 26, flexShrink: 0, fontSize: 13, fontWeight: 600, color: C.muted, ...tnum }}>{it.quantity}×</Box>
          <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
            <Box sx={{ fontSize: 13, fontWeight: 500, color: C.text }}>{it.title}</Box>
            {it.variation?.title && <Box sx={{ fontSize: 12, color: C.muted }}>{it.variation.title}</Box>}
            {(it.addons || []).map(a => (a.options || []).map(op => (
              <Box key={op._id || op.title} sx={{ fontSize: 12, color: C.muted }}>+ {op.title} <span style={{ fontVariantNumeric: 'tabular-nums' }}>({money(op.price)})</span></Box>
            )))}
            {it.specialInstructions && <Box sx={{ fontSize: 12, fontWeight: 500, color: AGE.warn.ink }}>Note: {it.specialInstructions}</Box>}
          </Box>
          <Box sx={{ fontSize: 13, color: C.text, ...tnum }}>{money(it.variation?.price)}</Box>
        </Box>
      ))}
    </Box>
  )
}

function Proof({ view }) {
  const o = view.raw
  const shots = view.external
    ? [view.proofPhoto && { label: 'Dropoff photo', src: view.proofPhoto }].filter(Boolean)
    : [
        o.pickupReceiptImage && { label: 'Pickup receipt', src: o.pickupReceiptImage },
        (o.deliveryProofImage || o.fdDropoffPhoto) && { label: 'Dropoff photo', src: o.deliveryProofImage || o.fdDropoffPhoto },
      ].filter(Boolean)
  if (!shots.length) return null
  return (
    <Section title="Proof of delivery">
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        {shots.map(s => (
          <Box key={s.label} sx={{ width: 140 }}>
            <Box sx={{ fontSize: 12, color: C.muted, mb: 0.5 }}>{s.label}</Box>
            <a href={s.src} target="_blank" rel="noopener noreferrer">
              <img src={s.src} alt={s.label} style={{ width: '100%', height: 120, objectFit: 'cover', borderRadius: 8, border: `1px solid ${C.border}`, cursor: 'zoom-in' }} />
            </a>
          </Box>
        ))}
      </Box>
    </Section>
  )
}

function Footer({ view }) {
  const ui = useDispatchUi()
  if (!isActive(view)) {
    const at = view.deliveredAt || view.collectedAt || view.cancelledAt
    return (
      <Box sx={{ px: 2.5, py: 1.5, borderTop: `1px solid ${C.border}`, fontSize: 12, color: C.muted }}>
        {view.status === 'COLLECTED' ? 'Collected by the customer' : statusMeta(view.status).label}
        {at ? ` ${fullStamp(at, view.restaurant?.timezone)}` : ''}. Nothing left to do on this order.
      </Box>
    )
  }
  // An email order moves only with First Delivery's updates; nothing to press.
  if (view.external === 'EMAIL') {
    return (
      <Box sx={{ px: 2.5, py: 1.5, borderTop: `1px solid ${C.border}`, fontSize: 12, color: C.muted }}>
        RushMyFood handles this delivery. It updates here on its own.
      </Box>
    )
  }
  // An open old-app order can still be completed or cancelled — that writes through to the old app.
  const next = view.external === 'EHUNGRY' ? { label: 'Mark completed', run: () => ui.setOldAppStatus(view, 'completed') } : NEXT_STEP[view.status]
  const stale = isStale(view, ui.now)
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2.5, py: 1.5, borderTop: `1px solid ${C.border}`, bgcolor: C.subtle }}>
      <Button
        size="small"
        variant={stale ? 'outlined' : 'text'}
        onClick={() => ui.requestCancel([view])}
        sx={{ height: 32, textTransform: 'none', fontWeight: 500, borderRadius: '8px', color: AGE.late.ink, borderColor: 'rgba(180,35,24,0.3)', '&:hover': { bgcolor: AGE.late.bg, borderColor: 'rgba(180,35,24,0.5)' } }}
      >
        Cancel order
      </Button>
      <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
        {next && canAssign(view) && <AssignButton view={view} prominent={false} />}
        {next ? (
          <Button size="small" onClick={() => (next.run ? next.run() : ui.setStatus([view], next.to))} sx={{ ...primaryButtonSx, height: 32, px: 1.75 }}>
            {next.label}
          </Button>
        ) : canAssign(view) ? (
          <AssignButton view={view} prominent />
        ) : null}
      </Box>
    </Box>
  )
}

function DrawerBody({ view, items, itemsLoading, mapboxToken, onClose, onStep, canStep }) {
  const ui = useDispatchUi()
  const now = ui.now
  const o = view.raw
  const level = ageLevel(view, now)
  const stale = isStale(view, now)
  const late = isLate(view, now)
  const [liveDriver, setLiveDriver] = useState(null)
  const [route, setRoute] = useState(null)
  const riderId = o.rider?._id

  useSubscription(SUB_RIDER_LOCATION, {
    variables: { riderId },
    skip: !riderId,
    onData: ({ data }) => {
      const c = data?.data?.subscriptionRiderLocation?.location?.coordinates
      if (c?.length >= 2) setLiveDriver({ lat: parseFloat(c[1]), lng: parseFloat(c[0]) })
    },
  })
  useEffect(() => {
    setLiveDriver(null)
    setRoute(null)
  }, [view.id])

  const dropoffAt = useDropoffLocation(view, mapboxToken)
  const mapView = view.address && dropoffAt !== view.address.location ? { ...view, address: { ...view.address, location: dropoffAt } } : view
  const miles = haversineMiles(view.restaurant.location, mapView.address?.location)
  const reason = view.external ? view.reason : o.reason
  // First Delivery's tracker and the delivery service's own (DoorDash, Grubhub…); either may be missing.
  const trackerLinks = view.trackerLinks || [o.fdTrackerLink, o.fdDspTrackerLink].filter(Boolean)
  const trackerSx = { display: 'inline-flex', alignItems: 'center', gap: 0.5, fontSize: 12, color: C.text }
  const iconBtn = { width: 30, height: 30, color: C.muted, borderRadius: '8px', '&:hover': { bgcolor: C.mutedBg } }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <Box component="header" sx={{ px: 2.5, pt: 2, pb: 1.5, borderBottom: `1px solid ${C.border}`, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
          <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
              <Box component="h2" sx={{ m: 0, fontFamily: MONO, fontSize: 18, fontWeight: 600, letterSpacing: '-0.01em', color: C.text, ...tnum }}>#{view.orderNo}</Box>
              <Tooltip title="Copy order #" arrow>
                <IconButton size="small" onClick={() => ui.copy(`#${view.orderNo}`, 'Order number')} aria-label="Copy order number" sx={{ ...iconBtn, width: 26, height: 26 }}>
                  <ContentCopyIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
              <StatusBadge status={view.status} />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, fontSize: 12, color: C.muted }}>
              <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, color: C.text }}>
                <RestaurantLogo restaurant={view.restaurant} size={16} />
                {view.restaurant.name}
              </Box>
              <span>·</span>
              <span>Placed {view.placedAt ? fullStamp(view.placedAt, view.restaurant?.timezone) : '—'}</span>
              {view.sourceLabel && (
                <>
                  <span>·</span>
                  <span>{view.sourceLabel}</span>
                </>
              )}
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
            <Tooltip title="Previous order (K or ↑)" arrow>
              <span><IconButton size="small" disabled={!canStep.prev} onClick={() => onStep(-1)} aria-label="Previous order" sx={iconBtn}><KeyboardArrowUpIcon fontSize="small" /></IconButton></span>
            </Tooltip>
            <Tooltip title="Next order (J or ↓)" arrow>
              <span><IconButton size="small" disabled={!canStep.next} onClick={() => onStep(1)} aria-label="Next order" sx={iconBtn}><KeyboardArrowDownIcon fontSize="small" /></IconButton></span>
            </Tooltip>
            <Tooltip title="Close (Esc)" arrow>
              <IconButton size="small" onClick={onClose} aria-label="Close details" sx={iconBtn}><CloseIcon fontSize="small" /></IconButton>
            </Tooltip>
          </Box>
        </Box>

        {(stale || late || view.total === 0 || reason) && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            {stale && (
              <Callout tone="stale" icon={<AccessTimeIcon />}>
                Open for {span(now - view.placedAt)} and still {statusMeta(view.status).label.toLowerCase()}. If it never went out, cancel it to clear it off the board.
              </Callout>
            )}
            {/* Stale orders already get the callout above; don't stack a second one. */}
            {late && !stale && (
              <Callout tone="late" icon={<AccessTimeIcon />}>
                {view.type === 'DELIVERY' ? 'Dropoff' : 'Pickup'} was due {relative(dueAt(view), now)}
                {!view.driver && view.type === 'DELIVERY' ? ' and nobody is carrying it' : ''}.
              </Callout>
            )}
            {view.total === 0 && (
              <Callout tone="warn" icon={<WarningAmberRoundedIcon />}>
                Total is $0.00 — no payment was captured. Check the ticket before dispatching.
              </Callout>
            )}
            {reason && <Callout tone="stale" icon={<WarningAmberRoundedIcon />}>Reason: {reason}</Callout>}
          </Box>
        )}
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: 2.5, py: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
        {view.type === 'DELIVERY' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <RouteMap view={mapView} mapboxToken={mapboxToken} liveDriver={liveDriver} onRoute={setRoute} />
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, fontSize: 12, color: C.muted, ...tnum }}>
              <AgeBadge view={view} level={level} now={now} />
              <span>
                {route?.distance ? `${(route.distance / 1609.34).toFixed(1)} mi by road · ${Math.round(route.duration / 60)} min drive` : miles != null ? `${miles.toFixed(1)} mi straight line` : ''}
              </span>
            </Box>
          </Box>
        )}

        <Section
          title="Delivery"
          aside={trackerLinks.length === 1 ? (
            <Link href={trackerLinks[0]} target="_blank" rel="noopener noreferrer" underline="hover" sx={trackerSx}>
              {ui.firstDelivery && o.fdTrackerLink ? 'Track on First Delivery' : 'Track delivery'} <OpenInNewIcon sx={{ fontSize: 13 }} />
            </Link>
          ) : trackerLinks.length > 1 ? (
            // Same names the Orders page gives the pair.
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              {trackerLinks.map((href, i) => (
                <Link key={href} href={href} target="_blank" rel="noopener noreferrer" underline="hover" sx={trackerSx}>
                  Tracker {i + 1} <OpenInNewIcon sx={{ fontSize: 13 }} />
                </Link>
              ))}
            </Box>
          ) : null}
        >
          <DeliveryBlock view={view} />
        </Section>

        <Section title="Status history"><Timeline view={view} now={now} /></Section>

        <Section title="Contacts">
          <Box sx={{ border: `1px solid ${C.border}`, borderRadius: '10px', '& > * + *': { borderTop: `1px solid ${C.borderSoft}` } }}>
            <ContactRow avatar={<RestaurantLogo restaurant={view.restaurant} size={32} />} title={view.restaurant.name} lines={[view.restaurant.address]} phone={view.restaurant.phone} />
            {view.customer ? (
              <ContactRow
                avatar={<PersonAvatar name={view.customer.name} size={32} />}
                title={view.customer.name}
                lines={[view.address ? view.address.full : view.type === 'PICKUP' ? `${view.typeLabel} — no delivery address` : null, view.address?.details, view.customer.email]}
                phone={view.customer.phone}
                sms
                extra={view.address ? <AddressActions full={view.address.full} /> : null}
              />
            ) : (
              <Box sx={{ px: 1.5, py: 1.5, fontSize: 12, color: C.muted }}>
                {view.type === 'PICKUP' ? 'Walk-in ticket — no customer details on file.' : 'No customer details on this order.'}
              </Box>
            )}
            {view.driver && (
              <ContactRow
                avatar={<PersonAvatar name={view.driver.name} photo={view.driver.photo} size={32} />}
                title={view.driver.name}
                lines={[view.driver.kind === 'FIRST_DELIVERY' && ui.firstDelivery ? 'First Delivery courier' : view.driver.kind === 'STAFF' ? 'Restaurant staff' : 'RushMyFood driver']}
                phone={view.driver.phone}
                sms
              />
            )}
          </Box>
        </Section>

        <Section title={`Items${items.length ? ` · ${items.reduce((n, it) => n + (parseInt(it.quantity, 10) || 1), 0)}` : ''}`}>
          <Items items={items} loading={itemsLoading} />
        </Section>

        <Section title="Payment"><Payment view={view} items={items} /></Section>

        <Proof view={view} />
      </Box>

      <Footer view={view} />
    </Box>
  )
}

/**
 * Right-hand order panel. Persistent (non-modal) so the dispatcher can click
 * another row, or press J/K, and the panel follows instead of closing.
 */
export default function OrderDrawer({ view, items, itemsLoading, mapboxToken, onClose, onStep, canStep }) {
  return (
    <Drawer
      anchor="right"
      variant="persistent"
      open={!!view}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 480 }, borderLeft: `1px solid ${C.border}`, boxShadow: '-16px 0 48px -12px rgba(16,24,40,0.18)',
          zIndex: 1250, color: C.text,
        },
      }}
    >
      {view && (
        <DrawerBody view={view} items={items} itemsLoading={itemsLoading} mapboxToken={mapboxToken} onClose={onClose} onStep={onStep} canStep={canStep} />
      )}
    </Drawer>
  )
}
